import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import Loading from 'components/shared/Utils/Loading';
import {ButtonInfo} from 'components/buttons';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {isCashbackLimitedTimeLocation} from 'helpers/functionUtils/cashbackLimitedTime';
import {
  CLEAR_CASHBACK_DATA,
  GET_CASHBACK_SETTINGS,
  GET_CASHBACK_SUMMARY,
  SAVE_CASHBACK_SETTINGS
} from 'store/cashback/action';
import CashbackEmptyState from 'routes/App/cashback/components/CashbackEmptyState';
import CashbackDashboard from 'routes/App/cashback/components/CashbackDashboard';
import MoreActionsMenu from 'components/MoreActionsMenu';
import CashbackSettingsModal from 'routes/App/cashback/components/CashbackSettingsModal';
import CashbackDisabledBanner from 'routes/App/cashback/components/CashbackDisabledBanner';
import {
  CashbackHeader,
  CashbackHeaderSubtitle
} from 'routes/App/cashback/styles';

const isSettingsForLocation = (settings, locationId) => {
  if (!settings?.id) {
    return false;
  }

  if (!Array.isArray(settings.locationIds)) {
    return true;
  }

  return settings.locationIds.includes(locationId);
};

const isCashbackSettingsEnabled = (settings, locationId) => {
  if (!settings?.id) {
    return false;
  }

  if (!isSettingsForLocation(settings, locationId)) {
    return false;
  }

  return (settings.status || 'enabled').toLowerCase() !== 'disabled';
};

const Cashback = () => {
  const dispatch = useDispatch();
  const [settingsModalMode, setSettingsModalMode] = useState(null);

  const serviceFeatures = useSelector(
    (state) => state.user.activeLocation?.AllowedServiceFeatures || []
  );
  const activeLocation = useSelector((state) => state.user.activeLocation);
  const locationId = useSelector((state) => state.user.activeLocation?._id);
  const userRole = useSelector((state) => state.user.userData?.role);
  const data = useSelector((state) => state.cashbackReducer.data);
  const loading = useSelector((state) => state.cashbackReducer.loading);
  const settings = useSelector((state) => state.cashbackReducer.settings);
  const settingsLoading = useSelector(
    (state) => state.cashbackReducer.settingsLoading
  );

  const hasStorefrontCashback = serviceFeatures.includes(
    FEATURE_RESTRICTION_IDS.STOREFRONT_CASHBACK
  );
  const canManageCashback =
    userRole === 'admin' && hasStorefrontCashback && locationId;
  const currentLocationSettings = isSettingsForLocation(settings, locationId)
    ? settings
    : null;
  const isCashbackEnabled = isCashbackSettingsEnabled(
    currentLocationSettings,
    locationId
  );
  const showTurnOffAction = !settingsLoading && isCashbackEnabled;
  const showDisabledCashbackBanner =
    hasStorefrontCashback &&
    !settingsLoading &&
    Boolean(currentLocationSettings?.id) &&
    !isCashbackEnabled;

  useEffect(() => {
    if (locationId) {
      dispatch(GET_CASHBACK_SETTINGS());
    }

    if (hasStorefrontCashback && locationId) {
      dispatch(GET_CASHBACK_SUMMARY());
    }
    return () => {
      dispatch(CLEAR_CASHBACK_DATA());
    };
  }, [hasStorefrontCashback, locationId]);

  const showDashboardSpinner = hasStorefrontCashback && loading && !data;
  const showLimitedTimeOffer =
    hasStorefrontCashback && isCashbackLimitedTimeLocation(activeLocation);

  return (
    <DashboardContent className='DashboardContent'>
      {showDisabledCashbackBanner && (
        <CashbackDisabledBanner
          onEnable={
            canManageCashback ? () => setSettingsModalMode('settings') : null
          }
        />
      )}
      <DashboardHeader>
        <CashbackHeader>
          <div>
            <Headline>Cashback Loyalty</Headline>
            {showLimitedTimeOffer && (
              <CashbackHeaderSubtitle>
                <span>
                  Track customer credits, redemptions, and repeat orders from
                  this page. Cashback is free for a limited time.
                </span>
                <ButtonInfo className='ButtonInfoDrop OutlineBig'>
                  <div className='DropInfo DropInfoUpCashback'>
                    Cashback is currently included for free as part of Ultimate
                    AI, our broader growth package. Contact us to learn more.
                  </div>
                </ButtonInfo>
              </CashbackHeaderSubtitle>
            )}
          </div>
          {canManageCashback && (
            <MoreActionsMenu
              options={[
                {
                  label: 'Settings',
                  onClick: () => setSettingsModalMode('settings')
                },
                {
                  label: 'Turn Off',
                  onClick: () => setSettingsModalMode('turnOff'),
                  hidden: !showTurnOffAction
                }
              ]}
            />
          )}
        </CashbackHeader>
      </DashboardHeader>

      {!hasStorefrontCashback && (
        <CashbackEmptyState
          showLimitedTimeOffer={isCashbackLimitedTimeLocation(activeLocation)}
        />
      )}
      {showDashboardSpinner && <Loading margin='80px 0' />}
      {hasStorefrontCashback && !showDashboardSpinner && (
        <CashbackDashboard data={data} />
      )}

      {canManageCashback && (
        <CashbackSettingsModal
          open={Boolean(settingsModalMode)}
          mode={settingsModalMode || 'settings'}
          settings={currentLocationSettings}
          settingsLoading={settingsLoading}
          locationId={locationId}
          outstandingCreditAmount={data?.creditsOutstanding?.value || 0}
          onClose={() => setSettingsModalMode(null)}
          onSaveSettings={(payload) =>
            dispatch(SAVE_CASHBACK_SETTINGS(payload))
          }
        />
      )}
    </DashboardContent>
  );
};

export default Cashback;
